import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Tag, Space, Typography, Alert, Popconfirm, message,
  Empty, Tabs, Select, Input, Form, Modal, DatePicker,
} from 'antd';
import {
  CheckOutlined, StopOutlined, ReloadOutlined, SafetyCertificateOutlined,
  FileSearchOutlined, PlusOutlined,
} from '@ant-design/icons';
import { efirmaAdminService } from '../../services/api';

const { Title, Text } = Typography;

const ESTADO_DISPOSITIVO = {
  pending: { color: 'orange', label: 'Pendiente' },
  authorized: { color: 'green', label: 'Autorizado' },
  revoked: { color: 'red', label: 'Revocado' },
};

const ESTADO_CONSENTIMIENTO = {
  solicitado: { color: 'orange', label: 'Solicitado' },
  otorgado: { color: 'green', label: 'Otorgado' },
  revocado: { color: 'red', label: 'Revocado' },
};

/**
 * Dispositivos, bitácora y constancias de e.firma.
 *
 * Autorizar un dispositivo se hace una vez; revisar qué se exportó es continuo.
 * Por eso la bitácora tiene el mismo peso que los dispositivos y no está
 * escondida en un rincón.
 */
const EfirmaDevices = () => {
  const [dispositivos, setDispositivos] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [constancias, setConstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cargandoLog, setCargandoLog] = useState(false);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState({ outcome: '', action: '' });
  const [modalConstancia, setModalConstancia] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTodo();
  }, []);

  // Las constancias sembradas para las e.firma de prueba llevan un alcance que
  // empieza por PRUEBA. Vigentes = otorgadas y no revocadas: una ya revocada no
  // habilita nada y no hay por qué seguir avisando de ella.
  const constanciasDePrueba = constancias.filter(
    (c) => (c.scope || '').startsWith('PRUEBA') && c.estado === 'otorgado'
  ).length;

  const fetchTodo = async () => {
    try {
      const [dev, log, cons] = await Promise.all([
        efirmaAdminService.listDevices(),
        efirmaAdminService.getAccessLog({ limit: 200 }),
        efirmaAdminService.listConsents(),
      ]);
      setDispositivos(dev?.dispositivos || []);
      setRegistros(log?.registros || []);
      setConstancias(cons?.constancias || []);
    } catch (err) {
      setError('Error al cargar la información de e.firma');
      console.error('Error fetching efirma admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const recargarLog = async (nuevosFiltros = filtros) => {
    setCargandoLog(true);
    try {
      const log = await efirmaAdminService.getAccessLog({ ...nuevosFiltros, limit: 200 });
      setRegistros(log?.registros || []);
    } catch (err) {
      message.error('No se pudo cargar la bitácora');
    } finally {
      setCargandoLog(false);
    }
  };

  const transicion = async (id, accion) => {
    try {
      if (accion === 'authorize') {
        await efirmaAdminService.authorizeDevice(id);
        message.success('Dispositivo autorizado');
      } else {
        await efirmaAdminService.revokeDevice(id);
        message.success('Dispositivo revocado');
      }
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo completar la operación');
    }
  };

  const registrarConstancia = async (values) => {
    setGuardando(true);
    try {
      await efirmaAdminService.recordConsent({
        client_id: values.client_id,
        scope: values.scope,
        granted_at: values.granted_at ? values.granted_at.format('YYYY-MM-DD HH:mm:ss') : undefined,
      });
      message.success('Constancia registrada');
      setModalConstancia(false);
      form.resetFields();
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo registrar la constancia');
    } finally {
      setGuardando(false);
    }
  };

  const columnasDispositivos = [
    {
      title: 'Dispositivo',
      dataIndex: 'name',
      key: 'name',
      render: (v, d) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {d.owner?.name || d.owner_client_id}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const e = ESTADO_DISPOSITIVO[s] || { color: 'default', label: s };
        return <Tag color={e.color}>{e.label}</Tag>;
      },
    },
    { title: 'Autorizado', dataIndex: 'authorized_at', key: 'authorized_at', render: (v) => v || '—' },
    { title: 'Revocado', dataIndex: 'revoked_at', key: 'revoked_at', render: (v) => v || '—' },
    {
      title: '',
      key: 'acciones',
      render: (_, d) => (
        <Space>
          {d.status === 'pending' && (
            <Popconfirm
              title="¿Autorizar este dispositivo?"
              description="Podrá recibir material de e.firma de los clientes en la cartera de su dueño. Confirma que reconoces el equipo."
              onConfirm={() => transicion(d.id, 'authorize')}
              okText="Autorizar"
              cancelText="Cancelar"
            >
              <Button type="primary" size="small" icon={<CheckOutlined />}>Autorizar</Button>
            </Popconfirm>
          )}
          {d.status !== 'revoked' && (
            <Popconfirm
              title="¿Revocar este dispositivo?"
              description="Dejará de poder exportar de inmediato. No recupera lo ya exportado."
              onConfirm={() => transicion(d.id, 'revoke')}
              okText="Revocar"
              cancelText="Cancelar"
            >
              <Button danger size="small" icon={<StopOutlined />}>Revocar</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const columnasLog = [
    { title: 'Momento', dataIndex: 'created_at', key: 'created_at', width: 170 },
    {
      title: 'Resultado',
      dataIndex: 'outcome',
      key: 'outcome',
      render: (o) => <Tag color={o === 'granted' ? 'green' : 'red'}>{o === 'granted' ? 'Concedido' : 'Rechazado'}</Tag>,
    },
    { title: 'Acción', dataIndex: 'action', key: 'action', render: (a) => <Tag>{a}</Tag> },
    { title: 'Actor', dataIndex: 'actor_client_id', key: 'actor', render: (v) => v || '—' },
    { title: 'Contribuyente', dataIndex: 'subject_client_id', key: 'subject', render: (v) => v || '—' },
    { title: 'Dispositivo', dataIndex: 'device_id', key: 'device', render: (v) => v || '—' },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      render: (v) => (v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : '—'),
    },
  ];

  const columnasConstancias = [
    { title: 'Contribuyente', dataIndex: 'client_id', key: 'client_id' },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (e) => {
        const c = ESTADO_CONSENTIMIENTO[e] || { color: 'default', label: e };
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    { title: 'Alcance', dataIndex: 'scope', key: 'scope' },
    { title: 'Solicitado', dataIndex: 'solicitado_en', key: 'solicitado_en', render: (v) => v || '—' },
    { title: 'Otorgado', dataIndex: 'otorgado_en', key: 'otorgado_en', render: (v) => v || '—' },
    {
      title: 'Desde',
      dataIndex: 'otorgado_desde',
      key: 'otorgado_desde',
      render: (v) => (v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : '—'),
    },
  ];

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  const pendientes = dispositivos.filter((d) => d.status === 'pending').length;
  const rechazos = registros.filter((r) => r.outcome === 'denied').length;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {pendientes > 0 && (
        <Alert
          type="info"
          showIcon
          message={`${pendientes} dispositivo${pendientes > 1 ? 's' : ''} pendiente${pendientes > 1 ? 's' : ''} de autorización`}
          description="Confirma con la persona que registró el equipo antes de autorizarlo. El sistema no puede verificar de quién es la máquina."
        />
      )}

      {/* Los rechazos son la señal útil: un actor probando fuera de su cartera
          se ve aquí, no en los éxitos. */}
      {rechazos > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${rechazos} intento${rechazos > 1 ? 's' : ''} rechazado${rechazos > 1 ? 's' : ''} en los últimos registros`}
          description="Revisa la bitácora: los rechazos muestran quién intentó alcanzar lo que no le corresponde."
        />
      )}

      <Card
        title={<Title level={4} style={{ margin: 0 }}><SafetyCertificateOutlined /> e.firma</Title>}
        extra={<Button icon={<ReloadOutlined />} onClick={fetchTodo}>Actualizar</Button>}
      >
        <Tabs
          items={[
            {
              key: 'dispositivos',
              label: `Dispositivos (${dispositivos.length})`,
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={dispositivos}
                  columns={columnasDispositivos}
                  pagination={false}
                  scroll={{ x: true }}
                  size="small"
                  locale={{ emptyText: <Empty description="Sin dispositivos registrados" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                />
              ),
            },
            {
              key: 'bitacora',
              label: <span><FileSearchOutlined /> Bitácora ({registros.length})</span>,
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space wrap>
                    <Select
                      allowClear
                      placeholder="Resultado"
                      style={{ width: 150 }}
                      value={filtros.outcome || undefined}
                      onChange={(v) => {
                        const f = { ...filtros, outcome: v || '' };
                        setFiltros(f);
                        recargarLog(f);
                      }}
                      options={[
                        { value: 'denied', label: 'Rechazados' },
                        { value: 'granted', label: 'Concedidos' },
                      ]}
                    />
                    <Input.Search
                      allowClear
                      placeholder="Acción (export, upload…)"
                      style={{ width: 220 }}
                      onSearch={(v) => {
                        const f = { ...filtros, action: v || '' };
                        setFiltros(f);
                        recargarLog(f);
                      }}
                    />
                  </Space>

                  <Table
                    rowKey="id"
                    loading={cargandoLog || loading}
                    dataSource={registros}
                    columns={columnasLog}
                    pagination={{ pageSize: 25, showSizeChanger: false }}
                    scroll={{ x: true }}
                    size="small"
                    locale={{ emptyText: <Empty description="Sin registros" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    La bitácora es de solo lectura: los registros no se modifican ni se borran
                    desde la aplicación.
                  </Text>
                </Space>
              ),
            },
            {
              key: 'constancias',
              label: `Constancias (${constancias.length})`,
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {/* La advertencia vive aquí y no en un TODO de un archivo:
                      quien tiene que actuar es quien mira esta pantalla. */}
                  {constanciasDePrueba > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      message={`${constanciasDePrueba} constancia${constanciasDePrueba > 1 ? 's' : ''} de PRUEBA vigente${constanciasDePrueba > 1 ? 's' : ''}`}
                      description="Corresponden a e.firma de prueba, sin contribuyente real detrás. Antes de operar con contribuyentes reales, revócalas: una constancia sembrada no es evidencia de que alguien autorizó."
                    />
                  )}

                  <Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalConstancia(true)}>
                      Registrar constancia
                    </Button>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Para quienes autorizaron al contratar. Usa la fecha del contrato, no la de hoy.
                    </Text>
                  </Space>

                  <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={constancias}
                    columns={columnasConstancias}
                    pagination={{ pageSize: 25, showSizeChanger: false }}
                    scroll={{ x: true }}
                    size="small"
                    locale={{ emptyText: <Empty description="Sin constancias" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                  />
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Registrar constancia de consentimiento"
        open={modalConstancia}
        onCancel={() => setModalConstancia(false)}
        onOk={() => form.submit()}
        confirmLoading={guardando}
        okText="Registrar"
        cancelText="Cancelar"
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Esta constancia afirma un hecho"
          description="Regístrala solo si el contribuyente autorizó de verdad, por contrato u otro documento. Es la evidencia que respalda haber usado su e.firma."
        />
        <Form form={form} layout="vertical" onFinish={registrarConstancia}>
          <Form.Item name="client_id" label="Id del contribuyente" rules={[{ required: true, message: 'Requerido' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="scope"
            label="Alcance"
            rules={[{ required: true, message: 'Requerido' }]}
            initialValue="Custodia y uso de e.firma ante el SAT"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="granted_at"
            label="Fecha en que autorizó"
            extra="La del contrato o documento. Si se omite, se usa la fecha de hoy."
          >
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default EfirmaDevices;
